
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Job } from './Interfaces';
import Select, { StylesConfig } from 'react-select';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { Alert } from 'react-bootstrap';



interface JobEditModalProps {
  show: boolean;
  onHide: () => void;
  job: Job;
}

interface FormValues {
    jobLabel: string;
    description: string;
    customerId: string[];
    creationDate: string;
    dueDate: string;
    status: string;
    lastEdited: string;
    customerName: string[];
  }

interface SelectOption {
    label: string;
    value: string;
  }

  // Define the type for the options
interface SelectOption {
    label: string;
    value: string;
  }
  

  const customSelectStyles: StylesConfig<SelectOption, true> = {
    control: (styles) => ({ 
      ...styles, 
      backgroundColor: 'var(--bs-secondary-bg)',
    }),
    menu: (styles) => ({ 
      ...styles, 
      backgroundColor: 'var(--bs-secondary-bg)',
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isFocused ? 'var(--bs-secondary-bg)' : isSelected ? 'var(--bs-secondary-bg)' : undefined,
      color: 'var(--bs-body-color)',
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--bs-secondary-bg)',
      },
    }),
    input: (styles) => ({
      ...styles,
      color: 'var(--bs-body-color)', // Set text color to white
    }),
    singleValue: (styles) => ({
      ...styles,
      color: 'var(--bs-body-color)', // Set text color to white for selected value
    }),
    placeholder: (styles) => ({
      ...styles,
      color: 'var(--bs-body-color)', // Set text color to white for placeholder
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: 'var(--bs-secondary-bg)',
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: 'var(--bs-body-color)',
    }),
  };

  

  const JobEditModal: React.FC<JobEditModalProps> = ({ show, onHide, job }) => {
    const [customers, setCustomers] = useState<SelectOption[]>([]);
    const [showSuccess, setShowSuccess] = useState(false);

 // Fetch all customers from the 'customers' collection
 useEffect(() => {
    const fetchCustomers = async () => {
      const querySnapshot = await getDocs(collection(db, 'customers'));
      const customerOptions: SelectOption[] = querySnapshot.docs.map(doc => ({
        label: doc.data().name,
        value: doc.id
      }));
      setCustomers(customerOptions);
    };
    fetchCustomers();
  }, []);


  const formik = useFormik<FormValues>({
    initialValues: {
        jobLabel: job.jobLabel,
        description: job.description,
        customerId: job.customerId,
        creationDate: job.creationDate,
        dueDate: job.dueDate,
        status: job.status,
        lastEdited: job.lastEdited,
        customerName: [],
      },
      validationSchema: Yup.object({
        jobLabel: Yup.string().required('Required'),
        description: Yup.string().required('Required'),
        customerId: Yup.array().of(Yup.string()).required('Required'),
        creationDate: Yup.string().required('Required'),
        dueDate: Yup.string().required('Required'),
        status: Yup.string().required('Required'),
        lastEdited: Yup.string().required('Required'),
        customerName: Yup.array().of(Yup.string()).required('Required'), 
      }),
      onSubmit: async values => {
        console.log(values);
      
        // Update existing job document. Legacy timer fields on the Job doc
        // (totalHours, unbilledHours, workSessions, accumulatedTimeMinutes) are
        // preserved as-is because we never touch them here.
        const updateData = {
          ...values,
          lastEdited: new Date().toISOString()
        };
        
        await updateDoc(doc(db, 'jobs', job.id), updateData as any);
      
        // Show the success notice
        setShowSuccess(true);
      
        // Close the modal and hide the success notice after 1 second
        setTimeout(() => {
          setShowSuccess(false);
          onHide();
        }, 1500);
      },
    });
  

    return (
        <Modal show={show} onHide={onHide} centered size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Edit Job: {job.jobLabel}</Modal.Title>
          </Modal.Header>
          
          <Modal.Body className="p-0">
            <div className="px-6 pb-6 pt-6">
                  <Form onSubmit={formik.handleSubmit}>
                    <div className="pt-6">
                      {showSuccess && <Alert variant="success">Changes saved successfully!</Alert>}
                      
                      <Form.Group controlId="formCustomerName" className="mb-4">
                        <Form.Label>Customer Name</Form.Label>
                        <Select
                          options={customers}
                          isMulti
                          onChange={(selectedOptions) => {
                            const values = Array.isArray(selectedOptions)
                              ? selectedOptions.map(option => option.value)
                              : [];
                            const names = Array.isArray(selectedOptions)
                              ? selectedOptions.map(option => option.label)
                              : [];
                            formik.setFieldValue('customerId', values);
                            formik.setFieldValue('customerName', names);
                          }}
                          styles={customSelectStyles}
                          placeholder="Select Customer(s)"
                          value={customers.filter((option: SelectOption) => formik.values.customerId.indexOf(option.value) !== -1)}
                          onBlur={() => formik.setFieldTouched('customerName', true)}
                        />
                        {formik.touched.customerName && formik.errors.customerName && (
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.customerName}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                      
                      <Form.Group controlId="formJobLabel" className="mb-4">
                        <Form.Label>Job Label</Form.Label>
                        <Form.Control
                          type="text"
                          name="jobLabel"
                          value={formik.values.jobLabel}
                          onChange={formik.handleChange}
                          isInvalid={formik.touched.jobLabel && !!formik.errors.jobLabel}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.jobLabel}
                        </Form.Control.Feedback>
                      </Form.Group>
              
                      <Form.Group controlId="formJobDescription" className="mb-4">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="description"
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          isInvalid={formik.touched.description && !!formik.errors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.description}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <div className="row">
                        <div className="col-md-6">
                          <Form.Group controlId="formDueDate" className="mb-4">
                            <Form.Label>Due Date</Form.Label>
                            <Form.Control
                              type="date"
                              name="dueDate"
                              value={formik.values.dueDate}
                              onChange={formik.handleChange}
                              isInvalid={formik.touched.dueDate && !!formik.errors.dueDate}
                            />
                            <Form.Control.Feedback type="invalid">
                              {formik.errors.dueDate}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </div>
                        
                        <div className="col-md-6">
                          <Form.Group controlId="formStatus" className="mb-4">
                            <Form.Label>Status</Form.Label>
                            <Form.Control
                              as="select"
                              name="status"
                              value={formik.values.status}
                              onChange={formik.handleChange}
                              isInvalid={formik.touched.status && !!formik.errors.status}
                            >
                              <option value="">Select status</option>
                              <option value="Pending">Pending</option>
                              <option value="Open">Open</option>
                              <option value="Awaiting Client Response">Awaiting Client Response</option>
                              <option value="Overdue">Overdue</option>
                              <option value="Closed">Closed</option>
                              <option value="Cancelled">Cancelled</option>
                              <option value="Archived">Archived</option>
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">
                              {formik.errors.status}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                              Current job status
                            </Form.Text>
                          </Form.Group>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <Form.Group controlId="formCreationDate" className="mb-4">
                            <Form.Label>Creation Date</Form.Label>
                            <Form.Control
                              type="date"
                              name="creationDate"
                              value={formik.values.creationDate}
                              onChange={formik.handleChange}
                              isInvalid={formik.touched.creationDate && !!formik.errors.creationDate}
                            />
                            <Form.Control.Feedback type="invalid">
                              {formik.errors.creationDate}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </div>
                      </div>

                      {/* Read-only fields section */}
                      <div className="border-top pt-4 mt-4">
                        <h6 className="text-muted mb-3">System Information</h6>
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group controlId="formJobId" className="mb-4">
                              <Form.Label>Job ID</Form.Label>
                              <Form.Control
                                type="text"
                                value={job.id}
                                readOnly
                                className="bg-light"
                              />
                              <Form.Text className="text-muted">
                                Unique identifier for this job
                              </Form.Text>
                            </Form.Group>
                          </div>
                          
                          <div className="col-md-6">
                            <Form.Group controlId="formLastEdited" className="mb-4">
                              <Form.Label>Last Edited</Form.Label>
                              <Form.Control
                                type="text"
                                value={new Date(job.lastEdited).toLocaleString()}
                                readOnly
                                className="bg-light"
                              />
                              <Form.Text className="text-muted">
                                When this job was last modified
                              </Form.Text>
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="d-flex justify-content-end pt-4 border-top">
                      <Button variant="secondary" onClick={onHide} className="me-3">
                        Close
                      </Button>
                      <Button variant="primary" type="submit">
                        Save Changes
                      </Button>
                    </div>
                  </Form>
            </div>
          </Modal.Body>
        </Modal>
      );
};

export default JobEditModal;